import {
  RaidPlanAssignmentDependency,
  RaidPlanAssignmentEntity,
  RaidPlanAssignmentType,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentAbilityKey,
} from 'app/frontend/types/custom/RaidPlan'
import {
  AssignmentScopeIndexes,
  getAssignmentScopeIndexes,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveExpectedEntities,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
  resolveAssignmentViewMode,
} from './assignmentTimeline'
import {
  getAnyPlayerFillerCount,
  getPlannedDisplayedCount,
} from './assignmentEntityCounting'

type DependencyRow = {
  abilityKey: RaidPlanEncounterAssignmentAbilityKey
  spellId: number
  assignmentGroups: number
  entries: { time: number }[]
}

export type DependencyEntry = {
  row: DependencyRow
  ability?: RaidPlanEncounterAssignmentAbility
}

export type DependencyGroupsByAbilityKey = Record<
  RaidPlanEncounterAssignmentAbilityKey,
  number
>

export type DependencyGraph = Map<
  RaidPlanEncounterAssignmentAbilityKey,
  Set<RaidPlanEncounterAssignmentAbilityKey>
>

export type TimedAssignmentScope = AssignmentScopeIndexes & { time: number }

export type AssignmentDependencyOption = {
  abilityKey: RaidPlanEncounterAssignmentAbilityKey
  groupIndex: number
  spellId: number
  isCustom: boolean
  assignmentType?: RaidPlanAssignmentType
  name: string
  color: string
  groupLabel?: string
  count: { min: number; max: number; disabling: boolean }
}

export type AssignmentDependencyData = {
  options: AssignmentDependencyOption[]
  graph: DependencyGraph
  groupsByAbilityKey: DependencyGroupsByAbilityKey
  getLockedCharacterIds: (
    dependency: RaidPlanAssignmentDependency,
    time: number,
  ) => Set<number> | null
}

export const isSameDependency = (
  left?: RaidPlanAssignmentDependency,
  right?: RaidPlanAssignmentDependency,
) =>
  left?.ability_key === right?.ability_key &&
  left?.group_index === right?.group_index

export const buildDependencyGroupsByAbilityKey = (
  entries: DependencyEntry[],
): DependencyGroupsByAbilityKey =>
  entries.reduce<DependencyGroupsByAbilityKey>((acc, { row, ability }) => {
    acc[row.abilityKey] = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      ability?.assignment_groups,
    )
    return acc
  }, {})

export const isValidDependency = (
  dependency: RaidPlanAssignmentDependency | undefined,
  selfKey: RaidPlanEncounterAssignmentAbilityKey,
  groupsByAbilityKey: DependencyGroupsByAbilityKey,
): dependency is RaidPlanAssignmentDependency => {
  if (!dependency || dependency.ability_key === selfKey) return false
  const { group_index: groupIndex } = dependency
  return (
    Number.isInteger(groupIndex) &&
    groupIndex >= 0 &&
    groupIndex < (groupsByAbilityKey[dependency.ability_key] ?? 0)
  )
}

export const getEffectiveDependency = (
  abilityDependency: RaidPlanAssignmentDependency | undefined,
  boxDependency: RaidPlanAssignmentDependency | undefined,
  selfKey: RaidPlanEncounterAssignmentAbilityKey,
  groupsByAbilityKey: DependencyGroupsByAbilityKey,
) => {
  if (isValidDependency(boxDependency, selfKey, groupsByAbilityKey)) {
    return boxDependency
  }
  if (isValidDependency(abilityDependency, selfKey, groupsByAbilityKey)) {
    return abilityDependency
  }
  return undefined
}

const getAbilityDependencies = (
  ability: RaidPlanEncounterAssignmentAbility | undefined,
  assignmentGroups: number,
) => {
  if (!ability) return []
  const boxDependencies = (ability.casts ?? []).flatMap((cast) =>
    (cast?.boxes ?? [])
      .slice(0, assignmentGroups)
      .map((box) => box?.depends_on),
  )
  return [ability.depends_on, ...boxDependencies].filter(
    (dependency): dependency is RaidPlanAssignmentDependency =>
      Boolean(dependency),
  )
}

export const buildDependencyGraph = (
  entries: DependencyEntry[],
  groupsByAbilityKey: DependencyGroupsByAbilityKey,
): DependencyGraph => {
  const graph: DependencyGraph = new Map()
  entries.forEach(({ row, ability }) => {
    const targets = new Set<RaidPlanEncounterAssignmentAbilityKey>()
    getAbilityDependencies(
      ability,
      groupsByAbilityKey[row.abilityKey] ?? 1,
    ).forEach((dependency) => {
      if (isValidDependency(dependency, row.abilityKey, groupsByAbilityKey)) {
        targets.add(dependency.ability_key)
      }
    })
    graph.set(row.abilityKey, targets)
  })
  return graph
}

export const getAbilityKeysReaching = (
  abilityKey: RaidPlanEncounterAssignmentAbilityKey,
  graph: DependencyGraph,
) => {
  const reaching = new Set<RaidPlanEncounterAssignmentAbilityKey>()
  const queue = [abilityKey]
  for (let index = 0; index < queue.length; index += 1) {
    const current = queue[index]
    graph.forEach((targets, sourceKey) => {
      if (!targets.has(current) || reaching.has(sourceKey)) return
      reaching.add(sourceKey)
      queue.push(sourceKey)
    })
  }
  return reaching
}

export const sortAbilityIndexesByDependencies = (
  entries: DependencyEntry[],
): number[] => {
  const groupsByAbilityKey = buildDependencyGroupsByAbilityKey(entries)
  const graph = buildDependencyGraph(entries, groupsByAbilityKey)
  const indexByAbilityKey = new Map(
    entries.map(({ row }, index) => [row.abilityKey, index]),
  )
  const state = new Map<number, 'visiting' | 'done'>()
  const order: number[] = []
  const visit = (index: number) => {
    if (state.has(index)) return
    state.set(index, 'visiting')
    const abilityKey = entries[index].row.abilityKey
    graph.get(abilityKey)?.forEach((targetKey) => {
      const targetIndex = indexByAbilityKey.get(targetKey)
      if (targetIndex !== undefined) visit(targetIndex)
    })
    state.set(index, 'done')
    order.push(index)
  }
  entries.forEach((_, index) => visit(index))
  return order
}

export const getTimedAssignmentScopes = ({
  row,
  ability,
}: DependencyEntry): TimedAssignmentScope[] => {
  const viewMode = resolveAssignmentViewMode(
    ability?.view_mode,
    row.entries.length,
    ability?.assignment_type,
    ability?.nsrt_interrupt_module,
  )
  return getAssignmentScopeIndexes(
    row.entries,
    viewMode,
    getEffectiveAbilityCustomGrouping(
      ability?.spell_id ?? row.spellId,
      ability?.custom_grouping,
    ),
    ability?.assignment_type,
  ).map((scope) => ({
    ...scope,
    time: row.entries[scope.castIndexes[0]]?.time ?? 0,
  }))
}

export const pickDependencyScope = <T extends { time: number }>(
  scopes: T[],
  time: number,
): T | undefined => {
  let picked: T | undefined
  scopes.forEach((scope) => {
    if (scope.time > time) return
    if (!picked || scope.time >= picked.time) picked = scope
  })
  return picked ?? scopes[0]
}

const getScopeBox = (
  ability: RaidPlanEncounterAssignmentAbility | undefined,
  scope: AssignmentScopeIndexes,
  assignmentGroups: number,
  groupIndex: number,
) =>
  normalizeAssignmentBox(
    normalizeAssignmentCast(
      ability?.casts?.[scope.castIndexes[0]],
      assignmentGroups,
    ).boxes[groupIndex],
  )

export const getDependencyGroupCount = (
  entry: DependencyEntry,
  scopes: TimedAssignmentScope[],
  groupIndex: number,
): AssignmentDependencyOption['count'] => {
  const assignmentGroups = getEffectiveAssignmentGroups(
    entry.row.assignmentGroups,
    entry.ability?.assignment_groups,
  )
  const counts = scopes.map((scope) => {
    const box = getScopeBox(entry.ability, scope, assignmentGroups, groupIndex)
    return getPlannedDisplayedCount(
      box.entities,
      getEffectiveExpectedEntities(
        entry.ability?.expected_entities,
        box.expected_entities,
      ),
    )
  })
  if (counts.length === 0) return { min: 0, max: 0, disabling: false }
  return {
    min: Math.min(...counts.map((count) => count.actual)),
    max: Math.max(...counts.map((count) => count.actual)),
    disabling: counts.some((count) => count.disabling),
  }
}

export const getLockedDependencyCharacterIds = (
  entry: DependencyEntry,
  scopes: TimedAssignmentScope[],
  groupIndex: number,
  time: number,
): Set<number> | null => {
  const scope = pickDependencyScope(scopes, time)
  if (!scope) return null
  const assignmentGroups = getEffectiveAssignmentGroups(
    entry.row.assignmentGroups,
    entry.ability?.assignment_groups,
  )
  const box = getScopeBox(entry.ability, scope, assignmentGroups, groupIndex)
  if (box.entities.length === 0) return null
  const isFullyExplicit = box.entities.every(
    (entity) => entity.kind === 'member',
  )
  if (!isFullyExplicit) return null
  const expected = getEffectiveExpectedEntities(
    entry.ability?.expected_entities,
    box.expected_entities,
  )
  if (getAnyPlayerFillerCount(box.entities, expected) > 0) return null
  return new Set(
    box.entities.flatMap((entity: RaidPlanAssignmentEntity) =>
      entity.kind === 'member' ? [entity.character_id] : [],
    ),
  )
}

export const getAvailableDependencyOptions = (
  data: AssignmentDependencyData,
  abilityKey: RaidPlanEncounterAssignmentAbilityKey,
) => {
  const reaching = getAbilityKeysReaching(abilityKey, data.graph)
  return data.options.filter(
    (option) =>
      option.abilityKey !== abilityKey && !reaching.has(option.abilityKey),
  )
}

export const findDependencyOption = (
  options: AssignmentDependencyOption[],
  dependency?: RaidPlanAssignmentDependency,
) =>
  dependency
    ? options.find(
        (option) =>
          option.abilityKey === dependency.ability_key &&
          option.groupIndex === dependency.group_index,
      )
    : undefined

export const getValidDependencyOption = (
  data: AssignmentDependencyData,
  selfKey: RaidPlanEncounterAssignmentAbilityKey,
  dependency?: RaidPlanAssignmentDependency,
) =>
  isValidDependency(dependency, selfKey, data.groupsByAbilityKey)
    ? findDependencyOption(data.options, dependency)
    : undefined

export const encodeDependencyValue = (
  dependency?: RaidPlanAssignmentDependency,
) =>
  dependency ? `${dependency.ability_key}:${dependency.group_index}` : undefined

export const decodeDependencyValue = (
  value?: string,
): RaidPlanAssignmentDependency | undefined => {
  if (!value) return undefined
  const separatorIndex = value.lastIndexOf(':')
  if (separatorIndex <= 0) return undefined
  const groupIndex = Number(value.slice(separatorIndex + 1))
  if (!Number.isInteger(groupIndex) || groupIndex < 0) return undefined
  return {
    ability_key: value.slice(0, separatorIndex),
    group_index: groupIndex,
  }
}
